import React, {Component, ReactElement} from 'react'
import {AdminPagePropsSet, createAdminPage} from '../../../components/shared/hoc/createAdminPage'
import {Nullable} from '../../../app/TypeHelpers'
import {NatAdminDisasterTopicForm} from './admin/NatAdminDisasterTopicForm'
import {ADMINISTORATOR_MODE, DISASTER_TOPIC_CATEGORY, MESSAGE_TYPE} from '../../../CodeMaster'
import {
    AdminLoginInfoSessionStorageKey,
    NatAdminLoginInfoSessionStorageHelper
} from '../../common/authentication/NatAdminLoginInfoSessionStorageHelper'
import NatCondominiumRepo from '../condominium/repository/NatCondominiumRepo'
import {NatPutAdminDisasterEditParams} from './repository/NatPutAdminDisasterTopicEdit'
import {Messages} from '../../../Messages'
import NatDisasterRepo from './repository/NatDisasterRepo'
import EventEmitterExAdmin from '../../../components/shared/pageBaseComponents/EventEmitterExAdmin'
import {TopicInfo} from './repository/NatGetDisasterTopicForAdmin'
import {Location} from 'react-router-dom'

interface Props {
    condominiumCd: string
    condominiumRepo: NatCondominiumRepo
    disasterRepo: NatDisasterRepo
    topicId: number
    eventEmitter: EventEmitterExAdmin
    location: Location
}

interface States {
    condominiumName: Nullable<string>
    category: string
    title: string
    description: Nullable<string>
    version: number
    isLoadCompleted: boolean
}

class NatAdminDisasterTopicEdit extends Component<Props & AdminPagePropsSet, States> {
    mode: Nullable<string>

    constructor(props: Props & AdminPagePropsSet) {
        super(props)
        this.mode = NatAdminLoginInfoSessionStorageHelper
            .getStringItemOrNull(AdminLoginInfoSessionStorageKey.SELECTED_MODE)

        this.state = {
            condominiumName: null,
            category: '',
            title: '',
            description: '',
            version: 0,
            isLoadCompleted: false
        }

        this.handleEditOkClick = this.handleEditOkClick.bind(this)
        this.handleEditTopicButtonClink = this.handleEditTopicButtonClink.bind(this)
        this.handleBackLinkClick = this.handleBackLinkClick.bind(this)
    }

    async componentDidMount(): Promise<void> {
        await this.props.eventEmitter.showLoading()
        try {
            if (this.mode === ADMINISTORATOR_MODE.ALL.value) {
                this.setState({condominiumName: await this.getCondominiumName()})
            }
            const topic = await this.getAdminDisasterTopic()
            if (topic) {
                this.setState({
                    category: topic.category,
                    title: topic.title,
                    description: topic.description,
                    version: topic.version
                })
            }
        } finally {
            this.props.eventEmitter.closeLoading()
            this.setState({isLoadCompleted: true})
        }
    }

    async getCondominiumName(): Promise<Nullable<string>> {
        const {condominiumCd, condominiumRepo, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            condominiumRepo.getCondominium.bind(condominiumRepo, condominiumCd),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            }
        )
        return response?.details?.condominiumName ?? null
    }

    async getAdminDisasterTopic(): Promise<Nullable<TopicInfo>> {
        const {condominiumCd, disasterRepo, topicId, eventEmitter} = this.props

        const response = await this.props.asyncCallFunction(
            disasterRepo.getAdminDisasterTopic.bind(disasterRepo, condominiumCd, topicId),
            async (httpErrorOrNetworkError) => {
                await httpErrorOrNetworkError.broadcast(eventEmitter)
            }
        )
        return response?.details ?? null
    }

    async handleEditTopicButtonClink(editParam: NatPutAdminDisasterEditParams): Promise<void> {
        await this.props.eventEmitter.showDialog({
            title: '確認',
            errorLevel: Messages.IOPCC0010.errorLevel,
            message: Messages.IOPCC0010.toString('トピック'),
            buttonProps: [
                {
                    isDefault: false,
                    buttonLabel: 'キャンセル',
                    buttonHandler: () => this.props.eventEmitter.closeDialog()
                },
                {
                    isDefault: false,
                    buttonLabel: 'OK',
                    buttonHandler: () => this.handleEditOkClick(editParam)
                }
            ]
        })
    }

    async handleEditOkClick(editParam: NatPutAdminDisasterEditParams): Promise<void> {
        const {condominiumCd, disasterRepo, topicId, eventEmitter} = this.props

        eventEmitter.closeDialog()
        await eventEmitter.showLoading()
        try {
            const response = await this.props.asyncCallFunction(
                disasterRepo.editDisasterTopic.bind(disasterRepo, condominiumCd, topicId, editParam),
                async (httpErrorOrNetworkError) => {
                    await httpErrorOrNetworkError.broadcast(eventEmitter)
                },
                {defaultErrorTitle: Messages.EOPCA0002.toString('トピック')}
            )

            if (response && response.requestError === null) {
                this.props.navigate(`/admin/condominiums/${condominiumCd}/disasters/topics/${topicId}`,
                    {
                        state: {
                            initialMessage: {
                                title: null,
                                messageLevel: MESSAGE_TYPE.INFORMATION,
                                detail: Messages.IOPCC0005.toString('トピック', editParam.title)
                            }
                        }
                    }
                )
            }
        } finally {
            this.props.eventEmitter.closeLoading()
        }
    }

    handleBackLinkClick(): void {
        const {condominiumCd, topicId} = this.props
        this.props.navigate(`/admin/condominiums/${condominiumCd}/disasters/topics/${topicId}`)
    }

    render(): ReactElement {
        if (!this.state.isLoadCompleted) {
            return <></>
        }
        return (
            <NatAdminDisasterTopicForm
                mode={'edit'}
                title={this.state.title}
                description={this.state.description}
                version={this.state.version}
                condominiumName={this.state.condominiumName}
                category={DISASTER_TOPIC_CATEGORY.codeToEnumConstants(this.state.category)}
                eventEmitter={this.props.eventEmitter}
                onEditorTopic={this.handleEditTopicButtonClink}
                backButtonText={'トピック詳細へ戻る'}
                onBack={this.handleBackLinkClick}
            />
        )
    }
}

export default createAdminPage(NatAdminDisasterTopicEdit)